import type { FormikErrors, FormikTouched } from "formik"
import { v4 as uuidv4 } from "uuid"

import { Operators } from "../../types/custom/projects"
import type { Rule } from "../../types/custom/rules"
import type { ConditionListFile } from "../../types/generated/api/ConditionListFile"
import type { RulesetRuleList } from "../../types/generated/api/RulesetRuleList"
import type { ScoreCardRule } from "../../types/generated/api/ScoreCardRule"
import type { TagListRuleList } from "../../types/generated/api/TagListRuleList"
import { WorkflowInputFeature } from "../../types/generated/api/WorkflowInputFeature"
import { multiValuesOperators } from "../../utils/conditionHelpers"
import { FILE_ID_MATCHER } from "../../utils/rulesetHelpers"
import type { ConditionPath, GroupConditions, ScoreCardFormikValues, ScorecardRule } from "./Interfaces"

type ConditionContainer = {
  conditions?: Rule[]
  groups?: Array<GroupConditions>
}

/**
 * Retrieves a specific condition from a scorecard rule by navigating through nested groups.
 *
 * @param values - The complete scorecard form values
 * @param ruleIndex - Index of the card in the cards array
 * @param path - Path object containing groupsIdx (array of group indices) and conditionIdx
 * @returns The condition at the specified path, or undefined if not found
 *
 * @example
 * // Get a root-level condition
 * getCondition(values, 0, { groupsIdx: [], conditionIdx: 0 })
 *
 * // Get a nested condition in groups
 * getCondition(values, 0, { groupsIdx: [0, 1], conditionIdx: 2 })
 */
export function getCondition(props: {
  values: ScoreCardFormikValues
  ruleIndex: number
  path: ConditionPath
}): Rule | undefined {
  const { path, ruleIndex, values } = props
  const card = values?.cards?.[ruleIndex]
  if (!card) return undefined

  const { groupsIdx, conditionIdx } = path

  if ((groupsIdx?.length ?? 0) === 0) {
    return card.conditions?.[conditionIdx]
  }

  let currentGroup: ConditionContainer | undefined = card as unknown as ConditionContainer
  for (const groupIndex of groupsIdx ?? []) {
    currentGroup = currentGroup?.groups?.[groupIndex]
    if (!currentGroup) return undefined
  }

  return currentGroup?.conditions?.[conditionIdx]
}

/**
 * Constructs a Formik field path string for accessing a condition's field value.
 * Handles both root-level conditions and deeply nested conditions within groups.
 *
 * @param field - The field name within the condition (e.g., 'value', 'operator')
 * @param ruleIndex - Index of the card in the cards array
 * @param path - Path object containing groupsIdx and conditionIdx
 * @returns A dot-notation string path for Formik field access
 *
 * @example
 * // Root level: "cards[0].conditions[1].value"
 * getFieldPath('value', 0, { groupsIdx: [], conditionIdx: 1 })
 *
 * // Nested: "cards[0].groups[0].groups[1].conditions[2].operator"
 * getFieldPath('operator', 0, { groupsIdx: [0, 1], conditionIdx: 2 })
 */
export function getFieldPath(props: { field: string; ruleIndex: number; path: ConditionPath }): string {
  const { field, path, ruleIndex } = props
  const { groupsIdx, conditionIdx } = path

  if ((groupsIdx?.length ?? 0) === 0) {
    return `cards[${ruleIndex}].conditions[${conditionIdx}].${field}`
  }

  const groupPath = (groupsIdx ?? [])
    .map((idx, level) => {
      return level === 0 ? `groups[${idx}]` : `.groups[${idx}]`
    })
    .join("")

  return `cards[${ruleIndex}].${groupPath}.conditions[${conditionIdx}].${field}`
}

/**
 * Retrieves the touched state for a specific condition from Formik's touched object.
 * Navigates through the nested structure following the same pattern as getCondition.
 *
 * @param touched - Formik's touched state object
 * @param ruleIndex - Index of the card in the cards array
 * @param path - Path object containing groupsIdx and conditionIdx
 * @returns The touched state for the condition, or undefined if not found
 */
export function getTouchedCondition(props: {
  touched: FormikTouched<ScoreCardFormikValues>
  ruleIndex: number
  path: ConditionPath
}): Rule | undefined {
  const { path, ruleIndex, touched } = props
  const card = touched?.cards?.[ruleIndex]
  if (!card || typeof card === "boolean") return undefined

  const { groupsIdx, conditionIdx } = path

  if ((groupsIdx?.length ?? 0) === 0) {
    return (card as unknown as ScorecardRule).conditions?.[conditionIdx] as unknown as Rule
  }

  let current: ConditionContainer | undefined = card as unknown as ConditionContainer
  for (const groupIndex of groupsIdx ?? []) {
    current = current?.groups?.[groupIndex]
    if (!current) return undefined
  }

  return current?.conditions?.[conditionIdx] as unknown as Rule
}

/**
 * Retrieves the error state for a specific condition from Formik's errors object.
 * Navigates through the nested structure following the same pattern as getCondition.
 *
 * @param errors - Formik's errors state object
 * @param ruleIndex - Index of the card in the cards array
 * @param path - Path object containing groupsIdx and conditionIdx
 * @returns The error state for the condition, or undefined if not found
 */
export function getErrorCondition(props: {
  errors: FormikErrors<ScoreCardFormikValues>
  ruleIndex: number
  path: ConditionPath
}): Rule | undefined {
  const { errors, path, ruleIndex } = props
  const card = errors?.cards?.[ruleIndex]
  if (!card || typeof card === "string") return undefined

  const { groupsIdx, conditionIdx } = path

  if ((groupsIdx?.length ?? 0) === 0) {
    return (card as unknown as ScorecardRule).conditions?.[conditionIdx] as unknown as Rule
  }

  let current: ConditionContainer | undefined = card as unknown as ConditionContainer
  for (const groupIndex of groupsIdx ?? []) {
    current = current?.groups?.[groupIndex]
    if (!current) return undefined
  }

  return current?.conditions?.[conditionIdx] as unknown as Rule
}

/**
 * Updates the value of a specific condition by navigating to it through nested groups.
 * Creates a shallow copy of values to maintain immutability.
 *
 * @param values - The complete scorecard form values
 * @param ruleIndex - Index of the card in the cards array
 * @param path - Path object containing groupsIdx and conditionIdx
 * @param newValue - The new value to set for the condition
 * @returns Updated scorecard form values with the modified condition
 */
export function updateConditionValue(props: {
  values: ScoreCardFormikValues
  ruleIndex: number
  path: ConditionPath
  newValue: string
}): ScoreCardFormikValues {
  const { newValue, path, ruleIndex, values } = props
  const updatedValues = { ...values }
  const card = updatedValues.cards?.[ruleIndex]
  if (!card) return updatedValues

  const { groupsIdx, conditionIdx } = path

  if ((groupsIdx?.length ?? 0) === 0) {
    if (card.conditions?.[conditionIdx]) {
      card.conditions[conditionIdx].value = newValue
    }
    return updatedValues
  }

  let currentGroup: ConditionContainer | undefined = card as unknown as ConditionContainer
  for (const groupIndex of groupsIdx ?? []) {
    currentGroup = currentGroup?.groups?.[groupIndex]
    if (!currentGroup) return updatedValues
  }

  if (currentGroup && currentGroup.conditions?.[conditionIdx]) {
    currentGroup.conditions[conditionIdx].value = newValue
  }

  return updatedValues
}

export function canRemoveCondition(props: {
  values: ScoreCardFormikValues
  ruleIndex: number
  path: ConditionPath
}): boolean {
  const { path, ruleIndex, values } = props
  const card = values?.cards?.[ruleIndex]
  if (!card) return false

  const { groupsIdx, conditionIdx } = path

  if ((groupsIdx?.length ?? 0) === 0) {
    return conditionIdx !== 0 || (card.conditions?.length || 0) > 1
  }

  return true
}

export function createEmptyCondition(): Rule {
  return {
    id: uuidv4(),
    feature: "",
    andOr: "and",
    operator: Operators["="],
    valueOrFeature: "Value",
    type: "string",
    value: "",
    secondValue: "",
    schemaFeatureType: WorkflowInputFeature.type.TEXT,
  }
}

/**
 * Determines the appropriate feature type for a new condition based on the operator and existing type.
 * Used when initializing new conditions or changing condition types.
 *
 * @param condition - Optional existing condition to derive type from
 * @returns The appropriate WorkflowInputFeature.type for the condition
 *
 * @example
 * // Returns LIST for 'between' or multi-value operators
 * getInitialNewFeatureType({ operator: 'between' })
 *
 * // Returns BOOLEAN for true/false types
 * getInitialNewFeatureType({ type: 'true' })
 */
export function getInitialNewFeatureType(condition?: Rule): WorkflowInputFeature.type {
  if ([Operators.between, ...multiValuesOperators].includes(condition?.operator as string)) {
    return WorkflowInputFeature.type.LIST
  } else if (condition?.type === "true" || condition?.type === "false") {
    return WorkflowInputFeature.type.BOOLEAN
  } else if (condition?.type === "number") {
    return WorkflowInputFeature.type.NUMBER
  } else {
    return WorkflowInputFeature.type.TEXT
  }
}

export function getOpValuefromTranslation(props: {
  translation: string
  t: (key: string) => string
}): string | undefined {
  const { t, translation } = props
  for (const value of Object.values(Operators)) {
    if (t(`operators.${value as string}`) === translation) {
      return value as string
    }
  }
}

/**
 * Removes a root-level condition and cleans up any associated file references.
 * Specifically handles conditions with file values by filtering out the file from condition_list_files.
 *
 * @param values - The complete scorecard form values
 * @param setValues - Formik's setValues function to update state
 * @param ruleIndex - Index of the card in the cards array
 * @param conditionIdx - Index of the condition to remove
 */
export function removeLevel1ConditionHandler(props: {
  values: ScoreCardFormikValues
  setValues: (values: React.SetStateAction<ScoreCardFormikValues>) => void
  ruleIndex: number
  conditionIdx: number
}): void {
  const { conditionIdx, ruleIndex, setValues, values } = props
  const removedConditionValue = values.cards[ruleIndex]?.conditions?.[conditionIdx]?.value
  const isFileValue = typeof removedConditionValue === "string" && removedConditionValue.includes("file")
  if (isFileValue) {
    const removedFileId = removedConditionValue?.match(FILE_ID_MATCHER)?.[1]
    setValues((prev: ScoreCardFormikValues) => {
      if (!prev.cards[ruleIndex]?.condition_list_files) return { ...prev }

      prev.cards[ruleIndex].condition_list_files = prev.cards[ruleIndex]?.condition_list_files.filter(
        (file: ConditionListFile) => file.uuid !== removedFileId,
      )
      return { ...prev }
    })
  }
}

/**
 * Navigates through nested groups to retrieve a specific group container.
 * Helper function for traversing the group hierarchy.
 *
 * @param card - The card or condition container to start from
 * @param groupsIdx - Array of group indices to traverse
 * @returns The group container at the specified path, or undefined if not found
 */
export function getGroupAtPath(props: {
  card: ConditionContainer | undefined
  groupsIdx: number[]
}): ConditionContainer | undefined {
  const { card, groupsIdx } = props
  if (!card || (groupsIdx?.length ?? 0) === 0) return card

  let current: ConditionContainer | undefined = card
  for (const groupIndex of groupsIdx) {
    current = current?.groups?.[groupIndex]
    if (!current) return undefined
  }

  return current
}

/**
 * Constructs a Formik field path string for accessing a group's field.
 * Similar to getFieldPath but for group-level fields rather than condition fields.
 *
 * @param ruleIndex - Index of the card in the cards array
 * @param groupsIdx - Array of group indices to traverse
 * @param field - The field name within the group (e.g., 'conditions', 'groups')
 * @returns A dot-notation string path for Formik field access
 */
export function getGroupFieldPath(props: { ruleIndex: number; groupsIdx: number[]; field: string }): string {
  const { field, groupsIdx, ruleIndex } = props
  if ((groupsIdx?.length ?? 0) === 0) {
    return `cards[${ruleIndex}].${field}`
  }

  const groupPath = groupsIdx
    .map((idx, level) => {
      return level === 0 ? `groups[${idx}]` : `.groups[${idx}]`
    })
    .join("")

  return `cards[${ruleIndex}].${groupPath}.${field}`
}

/**
 * Handles the removal of a condition with complex logic for maintaining rule structure integrity.
 *
 * This function implements several intelligent behaviors:
 * - Cleans up associated file references for conditions with file values
 * - Converts complex rules to simple rules when only one condition remains at root level
 * - Migrates conditions to parent level when a group would have only one condition remaining
 * - Removes entire groups when they become empty or have insufficient conditions
 * - Preserves nested groups by promoting them to parent level when their container is removed
 *
 * @param values - The complete scorecard form values
 * @param setFieldValue - Formik's setFieldValue function for targeted updates
 * @param setValues - Formik's setValues function for batch updates
 * @param levelRemove - Function to remove a condition at the current level (from FieldArray)
 * @param ruleIndex - Index of the card in the cards array
 * @param path - Path object containing groupsIdx and conditionIdx
 *
 * @example
 * // Remove a root-level condition (may convert to simple rule)
 * handleRemoveCondition(values, setFieldValue, setValues, remove, 0, { groupsIdx: [], conditionIdx: 1 })
 *
 * // Remove a nested condition (may trigger group cleanup)
 * handleRemoveCondition(values, setFieldValue, setValues, remove, 0, { groupsIdx: [0, 1], conditionIdx: 0 })
 */
export function handleRemoveCondition(props: {
  values: ScoreCardFormikValues
  setFieldValue: (field: string, value: unknown) => void
  setValues: (values: React.SetStateAction<ScoreCardFormikValues>) => void
  levelRemove: (idx: number) => void
  ruleIndex: number
  path: ConditionPath
}): void {
  const { levelRemove, path, ruleIndex, setFieldValue, setValues, values } = props
  const { groupsIdx, conditionIdx } = path

  const conditionToRemove = getCondition({ values, ruleIndex, path })
  const removedConditionValue = conditionToRemove?.value

  const isFileValue = typeof removedConditionValue === "string" && removedConditionValue.includes("file")
  if (isFileValue) {
    const removedFileId = removedConditionValue?.match(FILE_ID_MATCHER)?.[1]
    setValues((prev: ScoreCardFormikValues) => {
      if (!prev.cards[ruleIndex]?.condition_list_files) return { ...prev }

      prev.cards[ruleIndex].condition_list_files = prev.cards[ruleIndex]?.condition_list_files.filter(
        (file: ConditionListFile) => file.uuid !== removedFileId,
      )

      return { ...prev }
    })
  }

  if ((groupsIdx?.length ?? 0) === 0) {
    // Root level removal logic (Scorecards-specific)
    // 1) Clean up any associated file (already handled above)
    // 2) If we have <= 2 conditions and no groups, convert this card to a simple rule
    const currentLevelLength = values?.cards[ruleIndex]?.conditions?.length || 0
    const hasNoGroups = (values?.cards[ruleIndex]?.groups?.length || 0) === 0

    if (currentLevelLength <= 2 && hasNoGroups) {
      const conditionIndexToBeMigrated = conditionIdx === 0 ? 1 : 0
      const cachedCondition = values?.cards[ruleIndex]?.conditions?.[conditionIndexToBeMigrated]

      const filteredScorecards = values?.cards?.filter((_, idx) => idx !== ruleIndex)

      const newRule = {
        type: "simple" as const,
        id: values?.cards[ruleIndex]?.id,
        name: values?.cards[ruleIndex]?.name,
        conditions: cachedCondition ? [cachedCondition] : [],
        groups: [],
        return_value: values?.cards[ruleIndex]?.return_value,
        condition_list_files: [],
        schemaFeatureType: values?.cards[ruleIndex]?.schemaFeatureType,
      }

      setFieldValue(`cards`, [...filteredScorecards, newRule])
      return
    }

    // Default behavior: remove the specific condition at root level
    levelRemove(conditionIdx)
    return
  }

  const currentGroup = getGroupAtPath({
    card: values.cards[ruleIndex] as unknown as ConditionContainer,
    groupsIdx: groupsIdx ?? [],
  })
  const currentConditionsLength = currentGroup?.conditions?.length || 0

  if (currentConditionsLength <= 2) {
    const conditionToMigrate = conditionIdx === 0 ? currentGroup?.conditions?.[1] : currentGroup?.conditions?.[0]

    if (conditionToMigrate) {
      const parentPath = (groupsIdx ?? []).slice(0, -1)
      if ((parentPath?.length ?? 0) === 0) {
        setFieldValue(`cards[${ruleIndex}].conditions`, [
          ...(values.cards[ruleIndex]?.conditions || []),
          conditionToMigrate,
        ])
      } else {
        const parentFieldPath = getGroupFieldPath({ ruleIndex, groupsIdx: parentPath, field: "conditions" })
        const parentGroup = getGroupAtPath({
          card: values.cards[ruleIndex] as unknown as ConditionContainer,
          groupsIdx: parentPath,
        })
        setFieldValue(parentFieldPath, [...((parentGroup as ConditionContainer)?.conditions || []), conditionToMigrate])
      }
    }

    // remove the entire group
    const groupIndex = groupsIdx?.[groupsIdx.length - 1]
    const parentPath = (groupsIdx ?? []).slice(0, -1)
    if ((parentPath?.length ?? 0) === 0) {
      setFieldValue(
        `cards[${ruleIndex}].groups`,
        (values.cards[ruleIndex]?.groups || []).filter((_, idx) => idx !== groupIndex),
      )
    } else {
      const parentFieldPath = getGroupFieldPath({ ruleIndex, groupsIdx: parentPath, field: "groups" })
      const parentGroup = getGroupAtPath({
        card: values.cards[ruleIndex] as unknown as ConditionContainer,
        groupsIdx: parentPath,
      })
      setFieldValue(
        parentFieldPath,
        (parentGroup?.groups || []).filter((_, idx) => idx !== groupIndex),
      )
    }

    if (currentGroup?.groups && currentGroup.groups.length > 0) {
      const parentPath = (groupsIdx ?? []).slice(0, -1)
      const parentGroupsPath =
        (parentPath?.length ?? 0) === 0
          ? `cards[${ruleIndex}].groups`
          : getGroupFieldPath({ ruleIndex, groupsIdx: parentPath, field: "groups" })

      const parentGroup =
        (parentPath?.length ?? 0) === 0
          ? (values.cards[ruleIndex] as unknown as ConditionContainer)
          : (getGroupAtPath({
              card: values.cards[ruleIndex] as unknown as ConditionContainer,
              groupsIdx: parentPath,
            }) as ConditionContainer)

      setFieldValue(parentGroupsPath, [...((parentGroup as ConditionContainer)?.groups || []), ...currentGroup.groups])
    }
  } else {
    levelRemove(conditionIdx)
  }
}

export function isScorecardRule(value: TagListRuleList | RulesetRuleList | ScoreCardRule): value is ScoreCardRule {
  return "return_value" in value
}
